<template>
  <div
    ref="tooltip"
    class="Tooltip relative group"
  >
    <div class="cursor-pointer" @click="showTooltip = !showTooltip">
      <slot />
    </div>
    <div
      class="tooltip type-xs-medium"
      :class="{
        'group-hover:block': showOnHover,
        'hidden': !showTooltip && !permanent,
        'bottom': position === 'bottom',
        'alignLeft': align === 'left',
        'whitespace-nowrap': noWrap
      }"
    >
      <div
        class="arrow"
        :class="{
          'bottom': position === 'bottom',
          'alignLeft': align === 'left',
        }"
      />
      <span v-html="text" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { onClickOutside } from '@vueuse/core';

type Props = {
  text: string;
  showOnHover?: boolean;
  position?: string;
  // center keeps the bubble centered on the trigger, left aligns it to the triggers
  // left edge so it can't be pushed off screen when the trigger sits near the edge
  align?: 'center' | 'left';
  permanent?: boolean;
  noWrap?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  showOnHover: false,
  position: 'top',
  align: 'center',
  permanent: false,
  noWrap: false,
});
const showTooltip = ref(false);
const tooltip = ref<HTMLElement>();

if (!props.permanent) {
  onClickOutside(tooltip, () => {
    showTooltip.value = false;
  });
}
</script>

<style scoped>
.tooltip {
  @apply min-w-176 absolute bg-lightest border border-light p-12 z-modalsContent text-center;
  left: 50%;
  transform: translateX(-50%);

  &:not(.bottom) {
    bottom: calc(100% + 12px);

    &::before {
      bottom: -19px;
    }
  }

  &::before {
    @apply border-light absolute border-[10px] left-1/2;
    content: '';
    border-color: #ffffff transparent transparent transparent;
    transform: translateX(-50%);
  }

  &.bottom {
    top: calc(100% + 12px);

    &::before {
      @apply rotate-180 left-[44.25%];
      top: -19px;
    }
  }

  &.alignLeft {
    left: -8px;
    max-width: calc(100vw - 24px);
    transform: none;

    /* center the arrow over the trigger instead of over the bubble */
    &::before {
      @apply left-[14px];
    }

    /* the bottom variant rotates the arrow and loses the centering translate */
    &.bottom::before {
      @apply left-4;
    }
  }
}

.arrow {
  @apply absolute w-0 h-0 left-1/2 z-below;
  border-left: 10px solid transparent;
  border-right: 10px solid transparent;
  transform: translateX(-50%);
}

.arrow:not(.bottom) {
  @apply -bottom-[10px];
  border-top: 10px solid #DEDDD9;
}

.arrow.bottom {
  @apply -top-[10px];
  border-bottom: 10px solid #DEDDD9;
}

.arrow.alignLeft {
  @apply left-[14px];
}
</style>
